// =============================================================================
// domain/confluenceEdge.ts — which of your tools actually carry the edge.
// -----------------------------------------------------------------------------
// A ranking of every confluence you tag, a stacking ladder asking whether
// layering them helps, and two pairwise grids. Ported from the old client's
// `computeConfluenceStats` + the two grid builders.
//
// ── 🚨 A WIN DEFINITION MUST BE ABLE TO SAY "I CANNOT TELL" ──
//
// This is the finding that changes the card's headline, and both of the old
// client's three definitions get it wrong in OPPOSITE directions:
//
//   • **1R+** needs a recorded R. The old client counts a trade with no R as a
//     **LOSS**. Measured on Josh's book: the zero-confluence bucket is 441
//     trades of which only 159 carry an R, so 282 automatic losses drag it to
//     **8%** — while every confluence-tagged bucket is 100% measurable. The
//     card then prints "Stacking helps — win rate climbs (8% → 29% → 46%)".
//     Among the trades the definition can actually judge, that first step is
//     **21%**. The climb is real; more than half of its first stride is not.
//
//   • **Hit target** reads `targetNotHonored !== true`, so a trade nobody has
//     reviewed counts as having **HIT ITS TARGET**. That flag is present on 14
//     of 500 trades. Switching to it reads ~97% everywhere.
//
// One rule fixes both: a verdict is `win`, `loss`, or **`unscored`**, and
// unscored trades leave the denominator and are COUNTED so the screen can say
// how much of the bucket it could actually judge. Absent is not zero and it is
// not one either — the same rule as PD-15, PD-16 and PD-24.
//
// ── ⚠ AN ORPHANED CONFLUENCE STILL RANKS ──
//
// A confluence deleted from the registry in Settings still exists on every trade
// tagged with it. The old client ranks it under its raw id — which is why Josh's
// card shows `volume-profile` and `volpro-lvn` sitting among `E-UPPER` and
// `STRUCTURED S/D`. Dropping those rows would quietly rewrite his history, so
// they stay; they are FLAGGED instead, because a slug in a list of labels reads
// as a bug rather than as a deleted tool.
//
// ── ⚠ A CATEGORY PARENT IS NOT A CONFLUENCE ──
//
// A parent WITH children is a heading; its active children carry the signal.
// Ranking it too counts one observation twice. This is why a naive count returns
// different buckets from the old client's.
//
// ── ⚠ NO WALL CLOCK, NO STORE (D26) ──
// =============================================================================

import { median } from './excursionStats';

/** Tagged trades below this and the card leads with a small-sample warning. */
export const CONFLUENCE_ROBUST_SAMPLE = 100;
/** Below this a ranking row is "small sample" and dimmed. */
export const CONFLUENCE_MIN_SAMPLE = 10;
/** Tagged trades needed before "My Data" is the default view. */
export const CONFLUENCE_MYDATA_THRESHOLD = 25;
/** A grid cell needs this many MEASURABLE R values, not this many trades. */
export const CONFLUENCE_CELL_MIN = 5;
/** How many confluences an axis may carry before it stops being readable. */
export const CONFLUENCE_AXIS_CAP = 7;
/** Rows on the pattern grid. */
export const CONFLUENCE_PATTERN_CAP = 6;

export type WinDefinition = '1r' | 'net' | 'target';
export type ConfluenceMetric = 'winrate' | 'expectancy' | 'medianR';

export const WIN_DEFINITIONS: readonly { key: WinDefinition; label: string }[] = [
  { key: '1r', label: '1R+' },
  { key: 'net', label: 'Net positive' },
  { key: 'target', label: 'Hit target' },
];

export const CONFLUENCE_METRICS: readonly { key: ConfluenceMetric; label: string }[] = [
  { key: 'winrate', label: 'Win Rate' },
  { key: 'expectancy', label: 'Expectancy' },
  { key: 'medianR', label: 'Median R' },
];

/**
 * 🚨 THREE OUTCOMES, NOT TWO. `unscored` means this definition cannot judge this
 * trade — see the header. Collapsing it into `loss` (what the old client does to
 * a trade with no R) or into `win` (what it does to an unreviewed target) is the
 * single biggest distortion on this card.
 */
export type WinVerdict = 'win' | 'loss' | 'unscored';

export interface ConfluenceTrade {
  netPnl?: number | null;
  rrRealized?: number | null;
  booleans?: Record<string, unknown> | null;
  patternId?: string | null;
}

const fin = (v: unknown): number | null => {
  if (v == null || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

export function winVerdict(t: ConfluenceTrade, def: WinDefinition): WinVerdict {
  if (def === 'net') {
    const p = fin(t.netPnl);
    if (p == null) return 'unscored';
    // ⚠ A SCRATCH IS UNSCORED, not a loss — the same rule the pattern
    // leaderboard already applies. "A day of scratches is not a 0% win rate."
    if (p === 0) return 'unscored';
    return p > 0 ? 'win' : 'loss';
  }
  if (def === 'target') {
    // 🚨 THE FLAG IS THREE-VALUED AND THE OLD CLIENT READS IT AS TWO. `true` is
    // a reviewed miss, `false` a reviewed hit, ABSENT means nobody has looked.
    // `!== true` calls all three a hit.
    const v = t.booleans?.['targetNotHonored'];
    if (v === true) return 'loss';
    if (v === false) return 'win';
    return 'unscored';
  }
  const r = fin(t.rrRealized);
  if (r == null) return 'unscored';
  return r >= 1 ? 'win' : 'loss';
}

export interface ScoredStats {
  /** Every trade in the group. */
  n: number;
  /** Of those, how many this win definition could judge. */
  scored: number;
  wins: number;
  losses: number;
  /** Over `scored` only. `null` when nothing could be judged. */
  winRate: number | null;
  /** Mean realised R. `null` when no trade recorded one. */
  expectancy: number | null;
  medianR: number | null;
  /** How many recorded an R — the population behind the two R figures. */
  rSample: number;
}

function scoreGroup(trades: readonly ConfluenceTrade[], def: WinDefinition): ScoredStats {
  let wins = 0; let losses = 0;
  const rs: number[] = [];
  for (const t of trades) {
    const v = winVerdict(t, def);
    if (v === 'win') wins += 1;
    else if (v === 'loss') losses += 1;
    const r = fin(t.rrRealized);
    if (r != null) rs.push(r);
  }
  const scored = wins + losses;
  return {
    n: trades.length,
    scored, wins, losses,
    winRate: scored ? (100 * wins) / scored : null,
    expectancy: rs.length ? rs.reduce((a, b) => a + b, 0) / rs.length : null,
    medianR: median(rs),
    rSample: rs.length,
  };
}

/** Read one metric off a group, in its own units. */
export function confluenceMetricValue(s: ScoredStats, m: ConfluenceMetric): number | null {
  return m === 'winrate' ? s.winRate : m === 'expectancy' ? s.expectancy : s.medianR;
}

export interface ConfluenceRankRow {
  id: string;
  name: string;
  /** ⚠ The registry no longer holds this id — the name IS the id. See the header. */
  orphan: boolean;
  stats: ScoredStats;
}

export interface StackingBucket {
  count: 0 | 1 | 2 | 3;
  label: string;
  stats: ScoredStats;
}

export interface ConfluenceEdge {
  /** Every trade in scope. */
  population: number;
  /** Of those, how many carry at least one confluence. */
  totalTagged: number;
  ranking: ConfluenceRankRow[];
  stacking: StackingBucket[];
  /** How many distinct confluences appear at all. */
  distinct: number;
  /** How many of those clear the per-row sample floor. The honest headline. */
  aboveFloor: number;
  /** How many ranked rows are orphans. */
  orphans: number;
}

/**
 * Resolve a trade's ACTIVE, NON-CATEGORY confluences.
 *
 * Returns ids paired with display names so an orphan can keep its id and be
 * flagged, rather than either vanishing or masquerading as a label.
 */
export interface ConfluenceResolver {
  /** `null` when the registry does not hold this id — an orphan. */
  nameOf: (id: string) => string | null;
  isCategory: (id: string) => boolean;
}

export interface ResolvedMark { id: string; name: string; orphan: boolean }

export function marksOf(
  activeIds: readonly string[], r: ConfluenceResolver,
): ResolvedMark[] {
  const out: ResolvedMark[] = [];
  for (const id of activeIds) {
    if (!id || r.isCategory(id)) continue;
    const name = r.nameOf(id);
    out.push({ id, name: name ?? id, orphan: name == null });
  }
  return out;
}

/**
 * The whole card, from a population and a way to read each trade's marks.
 *
 * ⚠ THE STACKING LADDER READS THE WHOLE POPULATION, not just tagged trades. The
 * zero-confluence bucket IS the baseline, and a ladder without it compares
 * tagged trades to each other and calls the result an edge.
 */
export function confluenceEdge<T extends ConfluenceTrade>(
  trades: readonly T[],
  marks: (t: T) => readonly ResolvedMark[],
  def: WinDefinition,
): ConfluenceEdge {
  const byConf = new Map<string, { name: string; orphan: boolean; trades: T[] }>();
  const buckets: T[][] = [[], [], [], []];
  let totalTagged = 0;

  for (const t of trades) {
    const ms = marks(t);
    // ⚠ DEDUPED BY ID. A trade cannot count twice toward one confluence, and a
    // legacy row can carry the same id in both stored shapes.
    const seen = new Set<string>();
    for (const m of ms) {
      if (seen.has(m.id)) continue;
      seen.add(m.id);
      const g = byConf.get(m.id) ?? { name: m.name, orphan: m.orphan, trades: [] };
      g.trades.push(t);
      byConf.set(m.id, g);
    }
    const k = Math.min(3, seen.size);
    (buckets[k] as T[]).push(t);
    if (seen.size > 0) totalTagged += 1;
  }

  const ranking: ConfluenceRankRow[] = [];
  for (const [id, g] of byConf) {
    ranking.push({ id, name: g.name, orphan: g.orphan, stats: scoreGroup(g.trades, def) });
  }

  const LABELS = ['0 confluences', '1 confluence', '2 confluences', '3+ confluences'];
  const stacking: StackingBucket[] = [0, 1, 2, 3].map((k) => ({
    count: k as 0 | 1 | 2 | 3,
    label: LABELS[k] as string,
    stats: scoreGroup(buckets[k] as T[], def),
  }));

  return {
    population: trades.length,
    totalTagged,
    ranking,
    stacking,
    distinct: ranking.length,
    aboveFloor: ranking.filter((r) => r.stats.n >= CONFLUENCE_MIN_SAMPLE).length,
    orphans: ranking.filter((r) => r.orphan).length,
  };
}

/** Ranking order: real samples first, then by the active metric, nulls last. */
export function sortConfluenceRanking(
  rows: readonly ConfluenceRankRow[], m: ConfluenceMetric,
): ConfluenceRankRow[] {
  return [...rows].sort((a, b) => {
    const sa = a.stats.n < CONFLUENCE_MIN_SAMPLE;
    const sb = b.stats.n < CONFLUENCE_MIN_SAMPLE;
    if (sa !== sb) return sa ? 1 : -1;
    const av = confluenceMetricValue(a.stats, m);
    const bv = confluenceMetricValue(b.stats, m);
    if (av == null && bv == null) return 0;
    if (av == null) return 1;
    if (bv == null) return -1;
    return bv - av;
  });
}

export type ConfluenceBadge =
  | { kind: 'small'; more: number }
  | { kind: 'underperformer' }
  | { kind: 'reliable' }
  | { kind: 'sufficient' }
  | { kind: 'limited' };

/**
 * ⚠ THE ORDER IS THE MEANING. "Small sample" comes first, so a row with four
 * trades and a −2R average is never labelled `Underperformer` — that would be a
 * verdict on an edge, delivered off four trades.
 */
export function confluenceBadge(s: ScoredStats): ConfluenceBadge {
  if (s.n < CONFLUENCE_MIN_SAMPLE) return { kind: 'small', more: CONFLUENCE_MIN_SAMPLE - s.n };
  if (s.expectancy != null && s.expectancy < 0) return { kind: 'underperformer' };
  if (s.n >= 50) return { kind: 'reliable' };
  if (s.n >= 25) return { kind: 'sufficient' };
  return { kind: 'limited' };
}

// ── the two grids ───────────────────────────────────────────────────────────

export type ConfluenceCell =
  | { kind: 'diagonal' }
  | { kind: 'redundant' }
  | { kind: 'insufficient'; n: number; rSample: number }
  | { kind: 'value'; medianR: number; n: number; rSample: number };

export interface ConfluenceGrid {
  cols: string[];
  rows: { label: string; cells: ConfluenceCell[] }[];
  /** How many cells carry a reading. Zero means the grid says nothing. */
  populated: number;
  /** Trades that fed it. */
  count: number;
}

/**
 * The heat scale, and it is ABSOLUTE rather than range-relative.
 *
 * ⚠ Deliberately unlike the pairing matrices next door: those compare dollars,
 * where only the spread across the grid means anything, while this compares R —
 * a unit with a fixed zero. +1.5R is a strong cell whether or not another cell
 * beat it, and a relative scale would paint the least-bad cell of a losing grid
 * as the strong end.
 */
export function confluenceHeatBand(v: number | null): string {
  if (v == null) return 'neu';
  if (v >= 1.5) return 'pos-3';
  if (v >= 1.0) return 'pos-2';
  if (v >= 0.25) return 'pos-1';
  if (v > -0.25) return 'neu';
  if (v >= -0.6) return 'neg-1';
  return 'neg-2';
}

const cellFor = (trades: readonly ConfluenceTrade[]): ConfluenceCell => {
  const rs = trades.map((t) => fin(t.rrRealized)).filter((r): r is number => r != null);
  // 🚨 THE FLOOR IS THE MEASURABLE R VALUES, NOT THE TRADES. The old client
  // gates on `rs.length` here and then PRINTS `n = trades.length`, so a cell can
  // advertise ten trades behind a median resting on five. PD-24, one card over.
  if (rs.length < CONFLUENCE_CELL_MIN) {
    return { kind: 'insufficient', n: trades.length, rSample: rs.length };
  }
  return { kind: 'value', medianR: median(rs) as number, n: trades.length, rSample: rs.length };
};

/**
 * Confluence × Confluence — the same set on both axes, median R where BOTH were
 * tagged. Symmetric, with a dead diagonal.
 */
export function confluencePairGrid<T extends ConfluenceTrade>(
  trades: readonly T[], marks: (t: T) => readonly ResolvedMark[],
  cap = CONFLUENCE_AXIS_CAP,
): ConfluenceGrid {
  const tagged = trades.filter((t) => marks(t).length > 0);
  const freq = new Map<string, number>();
  for (const t of tagged) {
    for (const m of new Set(marks(t).map((x) => x.name))) freq.set(m, (freq.get(m) ?? 0) + 1);
  }
  const cols = [...freq.entries()].sort((a, b) => b[1] - a[1]).slice(0, cap).map(([k]) => k);

  let populated = 0;
  const rows = cols.map((rc) => ({
    label: rc,
    cells: cols.map((cc): ConfluenceCell => {
      if (rc === cc) return { kind: 'diagonal' };
      const both = tagged.filter((t) => {
        const ns = new Set(marks(t).map((x) => x.name));
        return ns.has(rc) && ns.has(cc);
      });
      const cell = cellFor(both);
      if (cell.kind === 'value') populated += 1;
      return cell;
    }),
  }));
  return { cols, rows, populated, count: tagged.length };
}

/**
 * Two names describe the same signal — a VWAP-reclaim setup tagged with a
 * VWAP-reclaim confluence adds nothing.
 *
 * ⚠ CONSERVATIVE ON PURPOSE: exact match after normalising, or one fully
 * containing the other. A looser rule would collapse real cells on live data,
 * and a wrongly-blanked cell is invisible — it looks like the pair was never
 * traded.
 */
export function confluenceRedundant(a: string, b: string): boolean {
  const norm = (s: string) => String(s || '').toLowerCase()
    .replace(/\([^)]*\)/g, '').replace(/[^a-z0-9]+/g, '');
  const na = norm(a); const nb = norm(b);
  if (!na || !nb) return false;
  return na === nb || na.includes(nb) || nb.includes(na);
}

/** Pattern × Confluence — rows are setups, cells are median R for trades with both. */
export function patternConfluenceGrid<T extends ConfluenceTrade>(
  trades: readonly T[],
  marks: (t: T) => readonly ResolvedMark[],
  patternName: (id: string) => string,
  rowCap = CONFLUENCE_PATTERN_CAP,
  colCap = CONFLUENCE_AXIS_CAP,
): ConfluenceGrid {
  const tagged = trades.filter((t) => marks(t).length > 0 && t.patternId);
  const pFreq = new Map<string, number>();
  const cFreq = new Map<string, number>();
  for (const t of tagged) {
    const pid = String(t.patternId);
    pFreq.set(pid, (pFreq.get(pid) ?? 0) + 1);
    for (const m of new Set(marks(t).map((x) => x.name))) cFreq.set(m, (cFreq.get(m) ?? 0) + 1);
  }
  const pids = [...pFreq.entries()].sort((a, b) => b[1] - a[1]).slice(0, rowCap).map(([k]) => k);
  const cols = [...cFreq.entries()].sort((a, b) => b[1] - a[1]).slice(0, colCap).map(([k]) => k);

  let populated = 0;
  const rows = pids.map((pid) => {
    const label = patternName(pid);
    return {
      label,
      cells: cols.map((c): ConfluenceCell => {
        if (confluenceRedundant(label, c)) return { kind: 'redundant' };
        const both = tagged.filter(
          (t) => String(t.patternId) === pid && marks(t).some((m) => m.name === c),
        );
        const cell = cellFor(both);
        if (cell.kind === 'value') populated += 1;
        return cell;
      }),
    };
  });
  return { cols, rows, populated, count: tagged.length };
}

// ── the stacking read ───────────────────────────────────────────────────────

export interface StackingRead {
  kind: 'climbs' | 'plateaus' | 'mixed' | 'unreadable';
  /** The three win rates the sentence quotes, when it quotes any. */
  steps: (number | null)[];
}

/**
 * What the ladder actually says.
 *
 * 🚨 `unreadable` IS A REAL ANSWER AND THE OLD CLIENT HAS NO SUCH BRANCH. Its
 * three cases are exhaustive, so it always produces a verdict — including on a
 * ladder built from buckets it could barely judge. Where a bucket has too few
 * SCORED trades to have a win rate at all, there is no ladder to read.
 */
export function stackingRead(buckets: readonly StackingBucket[]): StackingRead {
  const wr = [0, 1, 2].map((i) => buckets[i]?.stats.winRate ?? null);
  const [a, b, c] = wr;
  if (a == null || b == null || c == null) return { kind: 'unreadable', steps: wr };
  if (c > b && b > a) return { kind: 'climbs', steps: wr };
  if (c <= b) return { kind: 'plateaus', steps: wr };
  return { kind: 'mixed', steps: wr };
}

// ── Preview Mode ────────────────────────────────────────────────────────────
//
// ⚠ A CURATED, FABRICATED DATASET, and it exists because the card is otherwise
// blank for a user who has tagged nothing. The old client's own banner calls it
// a demo; it is designed to TEACH the metric — a clear winner, an
// underperformer, a small-sample trap at n=8 with a flashy win rate, and a
// stacking curve that climbs and then thins.
//
// 🚨 IT IS NEVER MIXED WITH REAL DATA and it is never the default once the user
// has enough of their own. The banner above it has to survive any redesign: a
// screenshot of this table alone is indistinguishable from a real book.
//
// ⚠ These figures are the old client's constants, transcribed — not invented
// here, and not to be "improved". They are the same numbers as its mockup, so
// the two clients' preview screenshots stay comparable.

const previewStats = (
  n: number, wr: number, expectancy: number, medianR: number,
): ScoredStats => ({
  n, scored: n, wins: Math.round((wr / 100) * n), losses: n - Math.round((wr / 100) * n),
  winRate: wr, expectancy, medianR, rSample: n,
});

export const CONFLUENCE_PREVIEW: ConfluenceEdge = {
  population: 204,
  totalTagged: 47,
  distinct: 7,
  aboveFloor: 7,
  orphans: 0,
  ranking: [
    { id: 'p-vwap', name: 'VWAP reclaim', orphan: false, stats: previewStats(80, 68, 0.7, 0.4) },
    { id: 'p-ob', name: 'OB confirmation', orphan: false, stats: previewStats(67, 61, 0.5, 0.3) },
    { id: 'p-sweep', name: 'Liquidity sweep', orphan: false, stats: previewStats(54, 59, 0.4, 0.2) },
    { id: 'p-vah', name: 'Daily VAH rejection', orphan: false, stats: previewStats(32, 56, 0.3, 0.1) },
    { id: 'p-fvg', name: 'FVG fill', orphan: false, stats: previewStats(41, 49, 0.1, -0.1) },
    { id: 'p-ctf', name: 'Counter-trend fade', orphan: false, stats: previewStats(28, 32, -0.6, -0.4) },
    { id: 'p-spike', name: 'Spike high test', orphan: false, stats: previewStats(8, 80, 1.4, 1.0) },
  ],
  stacking: [
    { count: 0, label: '0 confluences', stats: previewStats(22, 38, -0.3, -0.2) },
    { count: 1, label: '1 confluence', stats: previewStats(98, 54, 0.2, 0.1) },
    { count: 2, label: '2 confluences', stats: previewStats(72, 66, 0.6, 0.4) },
    { count: 3, label: '3+ confluences', stats: previewStats(12, 73, 0.9, 0.6) },
  ],
};

const pv = (medianR: number, n: number): ConfluenceCell =>
  ({ kind: 'value', medianR, n, rSample: n });
const pi = (n: number): ConfluenceCell => ({ kind: 'insufficient', n, rSample: n });
const pd: ConfluenceCell = { kind: 'diagonal' };

const PREVIEW_AXIS = [
  'VWAP reclaim', 'OB confirmation', 'Liquidity sweep',
  'Daily VAH rej.', 'FVG fill', 'Counter-trend fade',
];

export const CONFLUENCE_PREVIEW_PAIR: ConfluenceGrid = {
  cols: PREVIEW_AXIS,
  count: 47,
  populated: 26,
  rows: [
    { label: 'VWAP reclaim', cells: [pd, pv(1.8, 35), pv(1.2, 28), pv(0.9, 22), pv(0.6, 24), pv(-0.3, 11)] },
    { label: 'OB confirmation', cells: [pv(1.8, 35), pd, pv(1.1, 26), pv(0.7, 18), pv(0.5, 20), pv(-0.4, 12)] },
    { label: 'Liquidity sweep', cells: [pv(1.2, 28), pv(1.1, 26), pd, pv(0.6, 14), pv(0.2, 12), pv(-0.3, 9)] },
    { label: 'Daily VAH rej.', cells: [pv(0.9, 22), pv(0.7, 18), pv(0.6, 14), pd, pi(3), pi(2)] },
    { label: 'FVG fill', cells: [pv(0.6, 24), pv(0.5, 20), pv(0.2, 12), pi(3), pd, pv(-0.7, 8)] },
    { label: 'Counter-trend fade', cells: [pv(-0.3, 11), pv(-0.4, 12), pv(-0.3, 9), pi(2), pv(-0.7, 8), pd] },
  ],
};

export const CONFLUENCE_PREVIEW_PATTERN: ConfluenceGrid = {
  cols: PREVIEW_AXIS,
  count: 47,
  populated: 28,
  rows: [
    { label: 'MOR (Morning ORB)', cells: [pv(1.6, 38), pv(1.4, 31), pv(0.9, 24), pv(0.5, 18), pv(0.2, 15), pv(-0.4, 8)] },
    { label: 'RB (Range Break)', cells: [pv(0.9, 26), pv(1.1, 24), pv(0.7, 20), pv(0.4, 16), pv(0.1, 14), pv(-0.2, 10)] },
    { label: 'NSF (News Spike Fade)', cells: [pv(0.4, 18), pv(-0.2, 14), pv(0.3, 16), pv(-0.1, 10), pv(-0.5, 8), pv(0.9, 12)] },
    { label: 'VWAP Reclaim', cells: [{ kind: 'redundant' }, pv(1.2, 28), pv(0.8, 22), pv(0.6, 18), pv(0.4, 14), pv(-0.3, 9)] },
    { label: 'ICT FVG (Fair Value Gap)', cells: [pv(0.7, 20), pv(0.9, 22), pv(0.5, 16), pv(0.3, 12), { kind: 'redundant' }, pv(-0.5, 8)] },
  ],
};
